import type { MantineColorsTuple } from '@mantine/core'
import {
  argbFromHex,
  MaterialDynamicColors as C,
  type DynamicColor,
  Hct,
  hexFromArgb,
  SchemeTonalSpot,
  TonalPalette,
} from '@material/material-color-utilities'
import type { Hue, Tone } from '@tnn/core'

/**
 * Colours follow Material 3: one seed colour per app becomes a full set of
 * light and dark roles (surfaces, containers, outlines…) with Google's own
 * colour library, so every app is consistent and accessible by construction.
 */
export interface Roles {
  primary: string
  onPrimary: string
  primaryContainer: string
  onPrimaryContainer: string
  secondaryContainer: string
  onSecondaryContainer: string
  tertiaryContainer: string
  onTertiaryContainer: string
  error: string
  onError: string
  errorContainer: string
  onErrorContainer: string
  surface: string
  surfaceContainerLowest: string
  surfaceContainerLow: string
  surfaceContainer: string
  surfaceContainerHigh: string
  surfaceContainerHighest: string
  onSurface: string
  onSurfaceVariant: string
  outline: string
  outlineVariant: string
  inverseSurface: string
  inverseOnSurface: string
  inversePrimary: string
  scrim: string
}

const roleColors: Record<keyof Roles, DynamicColor> = {
  primary: C.primary,
  onPrimary: C.onPrimary,
  primaryContainer: C.primaryContainer,
  onPrimaryContainer: C.onPrimaryContainer,
  secondaryContainer: C.secondaryContainer,
  onSecondaryContainer: C.onSecondaryContainer,
  tertiaryContainer: C.tertiaryContainer,
  onTertiaryContainer: C.onTertiaryContainer,
  error: C.error,
  onError: C.onError,
  errorContainer: C.errorContainer,
  onErrorContainer: C.onErrorContainer,
  surface: C.surface,
  surfaceContainerLowest: C.surfaceContainerLowest,
  surfaceContainerLow: C.surfaceContainerLow,
  surfaceContainer: C.surfaceContainer,
  surfaceContainerHigh: C.surfaceContainerHigh,
  surfaceContainerHighest: C.surfaceContainerHighest,
  onSurface: C.onSurface,
  onSurfaceVariant: C.onSurfaceVariant,
  outline: C.outline,
  outlineVariant: C.outlineVariant,
  inverseSurface: C.inverseSurface,
  inverseOnSurface: C.inverseOnSurface,
  inversePrimary: C.inversePrimary,
  scrim: C.scrim,
}

function scheme(seed: string, dark: boolean) {
  return new SchemeTonalSpot(Hct.fromInt(argbFromHex(seed)), dark, 0)
}

export function roles(seed: string, dark: boolean): Roles {
  const s = scheme(seed, dark)
  const out = {} as Roles
  for (const [name, color] of Object.entries(roleColors) as [keyof Roles, DynamicColor][]) {
    out[name] = hexFromArgb(color.getArgb(s))
  }
  return out
}

/** Mantine wants ten shades, light to dark: M3 tones 95 → 10. */
const SHADE_TONES = [95, 90, 80, 70, 60, 50, 40, 30, 20, 10] as const

function shades(palette: TonalPalette): MantineColorsTuple {
  return SHADE_TONES.map((t) => hexFromArgb(palette.tone(t))) as unknown as MantineColorsTuple
}

export function primaryShades(seed: string): MantineColorsTuple {
  return shades(scheme(seed, false).primaryPalette)
}

export function neutralShades(seed: string): MantineColorsTuple {
  return shades(scheme(seed, false).neutralVariantPalette)
}

/**
 * Ordered 5-step ramp of the app colour for ordinal charts (pipeline stages,
 * aging buckets). Tones were checked with the dataviz palette validator:
 * monotone lightness, visible steps, light end ≥ 2:1 against the surface.
 */
export function ordinalRamp(seed: string, dark: boolean): string[] {
  const tones = dark ? [36, 48, 60, 72, 84] : [74, 62, 50, 39, 28]
  const palette = scheme(seed, dark).primaryPalette
  return tones.map((t) => hexFromArgb(palette.tone(t)))
}

/** Fixed hues for meaning that must not change between apps. */
const toneSeeds: Record<Exclude<Tone, 'progress' | 'neutral'>, string> = {
  info: '#1A73E8',
  success: '#1E8E3E',
  warning: '#E8A10C',
  danger: '#D93025',
}

export function semanticShades(): Record<keyof typeof toneSeeds, MantineColorsTuple> {
  const out = {} as Record<keyof typeof toneSeeds, MantineColorsTuple>
  for (const [tone, hex] of Object.entries(toneSeeds) as [keyof typeof toneSeeds, string][]) {
    const hct = Hct.fromInt(argbFromHex(hex))
    out[tone] = shades(TonalPalette.fromHueAndChroma(hct.hue, Math.max(hct.chroma, 48)))
  }
  return out
}

export interface ToneColors {
  bg: string
  fg: string
  dot: string
}

/** Container/on-container pair for status pills, light and dark. */
export function toneColors(seed: string, dark: boolean): Record<Tone, ToneColors> {
  const s = scheme(seed, dark)
  const pair = (palette: TonalPalette): ToneColors =>
    dark
      ? { bg: hexFromArgb(palette.tone(30)), fg: hexFromArgb(palette.tone(90)), dot: hexFromArgb(palette.tone(80)) }
      : { bg: hexFromArgb(palette.tone(92)), fg: hexFromArgb(palette.tone(30)), dot: hexFromArgb(palette.tone(45)) }
  const fixed = (hex: string) => {
    const hct = Hct.fromInt(argbFromHex(hex))
    return pair(TonalPalette.fromHueAndChroma(hct.hue, Math.max(hct.chroma, 48)))
  }
  const neutral = pair(s.neutralVariantPalette)
  return {
    neutral: { ...neutral, bg: dark ? hexFromArgb(s.neutralVariantPalette.tone(25)) : hexFromArgb(s.neutralVariantPalette.tone(94)) },
    info: fixed(toneSeeds.info),
    progress: pair(s.primaryPalette),
    success: fixed(toneSeeds.success),
    warning: fixed(toneSeeds.warning),
    danger: fixed(toneSeeds.danger),
  }
}

/** Seeds of the colours records wear as their own (`Hue`): apart from one another, the same in every app */
const hueSeeds: Record<Hue, string> = {
  red: '#D93025',
  orange: '#E8710A',
  amber: '#E8A10C',
  lime: '#7CB342',
  green: '#1E8E3E',
  teal: '#12A4AF',
  cyan: '#0B9AD6',
  blue: '#1A73E8',
  indigo: '#5562D6',
  violet: '#9334E6',
  pink: '#D01884',
  grey: '#80868B',
}

/** Container/on-container pairs of the record colours, as `toneColors` makes them; grey keeps its low chroma */
export function hueColors(dark: boolean): Record<Hue, ToneColors> {
  const out = {} as Record<Hue, ToneColors>
  for (const [hue, hex] of Object.entries(hueSeeds) as [Hue, string][]) {
    const hct = Hct.fromInt(argbFromHex(hex))
    const palette = TonalPalette.fromHueAndChroma(hct.hue, hue === 'grey' ? hct.chroma : Math.max(hct.chroma, 48))
    out[hue] = dark
      ? { bg: hexFromArgb(palette.tone(30)), fg: hexFromArgb(palette.tone(90)), dot: hexFromArgb(palette.tone(80)) }
      : { bg: hexFromArgb(palette.tone(92)), fg: hexFromArgb(palette.tone(30)), dot: hexFromArgb(palette.tone(45)) }
  }
  return out
}

/** Hues of the initials avatars (people and companies), kept from the first palette */
const AVATAR_HUES = [
  '#3E7FA6',
  '#3E9C8F',
  '#7C6BB8',
  '#C2617A',
  '#B0714A',
  '#5E8C4A',
  '#4A7FC1',
  '#8A5BA6',
  '#A0794A',
  '#5B8A86',
  '#D0663E',
  '#4F6FA8',
]

/**
 * Avatar backgrounds at Material tone 40: the same perceived lightness for every hue,
 * so white initials read at about 6.5:1 on all of them, in both schemes.
 */
export function avatarColors(): string[] {
  return AVATAR_HUES.map((hex) => {
    const hct = Hct.fromInt(argbFromHex(hex))
    return hexFromArgb(TonalPalette.fromHueAndChroma(hct.hue, Math.min(hct.chroma, 40)).tone(40))
  })
}
